import { ApiMode } from '@kaa/api/common';
import { RegionCode } from '@kaa/api/providers';
import { AuthProvider, AuthService, AuthServiceMock } from '@kaa/auth/common';
import {
  Logout,
  ChangeTitleRoute,
  CommunicationBanners,
} from '@kaa/core-app/common/components';
import { ActionProvider, AlertProvider } from '@kaa/common/context';

import { BuildConfig, getConfig } from '@kaa/core-app/common/config';
import {
  ActiveRouteProvider,
  LoadAuthUser,
  LoadUser,
  PageViewGTMListener,
  PrivateRoute,
} from '@kaa/core-app/providers/components';
import { UserAccessTokenExpiringListener } from '@kaa/core-app/providers/components/headless/UserAccessTokenExpiringListener';
import { UserSignedOutListener } from '@kaa/core-app/providers/components/headless/UserSignedOutListener';
import { TITLE_ROUTES } from '@kaa/core-app/providers/constants/titleRoutes';
import { getI18nInstance } from '@kaa/core-app/providers/i18n';
import { getPath, getRouterPath, Routes } from '@kaa/core-app/providers/routes';
import {
  ActivitiesScreen,
  ContactPersonDetailScreen,
  ContactPersonsScreen,
  DashboardScreen,
  EnterpriseRootScreen,
  EnterpriseScreen,
  ForbiddenWrongRegion,
  LoginScreen,
  NotFoundScreen,
  PickupBillingScreen,
  PrestationsHistoryScreen,
  PrestationsCustomerStatusScreen,
  PrestationsScreen,
  RelativeDetailsScreen,
  RemittancesReimbursementScreen,
  RemittancesScreen,
  ServiceUnavailableScreen,
  VideosScreen,
  VisibilityScreen,
  WorkersDetailsScreen,
  WorkersRootScreen,
  WorkersScreen,
  MaintenanceScreen,
} from '@kaa/core-app/providers/screens';
import { SwMain, SwPage } from '@kaa/ui-flanders/components';
import { Redirect, Router } from '@reach/router';
import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { ChangeLanguage } from './components/headless/ChangeLanguage';
import { getRemoteConfigByEnvironment } from './firebase-config';

export const App: React.FC = () => {
  const config = getConfig();

  if (!config) {
    return <ServiceUnavailableScreen />;
  }

  const {
    app: {
      mode,
      environment,
      auth: { oidc },
    },
    buildConfig,
  } = config;

  const {
    regionCode,
    i18n: { availableLanguages, fallbackLanguage },
  } = buildConfig;

  const remoteConfig = getRemoteConfigByEnvironment(environment);

  const initialState =
    mode === ApiMode.MOCK
      ? new AuthServiceMock()
      : new AuthService(oidc, buildConfig as BuildConfig<RegionCode>);

  return (
    <div className="App vl-u-sticky-gf">
      <CommunicationBanners remoteConfig={remoteConfig} />

      <I18nextProvider i18n={getI18nInstance()}>
        <AuthProvider initialState={initialState}>
          <ActionProvider>
            <AlertProvider>
              <PageViewGTMListener />
              <ChangeLanguage
                availableLanguages={availableLanguages}
                fallbackLanguage={fallbackLanguage}
              >
                <UserSignedOutListener
                  redirectUrl={getRouterPath(Routes.LOGIN)}
                />
                <UserAccessTokenExpiringListener />
                <LoadAuthUser />
                <LoadUser />
                <Header
                  languages={availableLanguages}
                  regionCode={regionCode as RegionCode}
                />
                <ActiveRouteProvider>
                  {({ activeRoute }) => (
                    <ChangeTitleRoute
                      activeRoute={activeRoute}
                      titleRoutes={TITLE_ROUTES}
                    />
                  )}
                </ActiveRouteProvider>
                <SwPage>
                  <SwMain>
                    <Router className="mainContent" primary={false}>
                      <NotFoundScreen path={getRouterPath(Routes.PAGE_404)} />
                      <ServiceUnavailableScreen
                        path={getRouterPath(Routes.SERVICE_UNAVAILABLE)}
                      />
                      <Redirect
                        from={getPath(Routes.LANDING)}
                        to={getPath(Routes.DASHBOARD)}
                        noThrow
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.DASHBOARD)}
                        component={DashboardScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.REMITTANCES)}
                        component={RemittancesScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.REMITTANCES_REIMBURSEMENT)}
                        component={RemittancesReimbursementScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.PRESTATIONS)}
                        component={PrestationsScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.PRESTATIONS_HISTORY)}
                        component={PrestationsHistoryScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.PRESTATIONS_CUSTOMER_STATUS)}
                        component={PrestationsCustomerStatusScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.WORKERS)}
                        component={WorkersScreen}
                      >
                        <Redirect
                          from="*"
                          to={getPath(Routes.WORKERS)}
                          noThrow
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path="/"
                          component={WorkersRootScreen}
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path={getRouterPath(Routes.WORKERS_DETAILS)}
                          component={WorkersDetailsScreen}
                        />
                      </PrivateRoute>
                      <Redirect
                        from="/pickup"
                        to={getPath(Routes.PICKUP_BILLING)}
                        noThrow
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.PICKUP_BILLING)}
                        component={PickupBillingScreen}
                      />
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.ENTERPRISE)}
                        component={EnterpriseScreen}
                      >
                        <Redirect
                          from="*"
                          to={getPath(Routes.ENTERPRISE)}
                          noThrow
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path="/"
                          component={EnterpriseRootScreen}
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path={getRouterPath(Routes.ENTERPRISE_ACTIVITIES)}
                          component={ActivitiesScreen}
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path={getRouterPath(Routes.ENTERPRISE_DETAILS)}
                          component={RelativeDetailsScreen}
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path={getRouterPath(
                            Routes.ENTERPRISE_DETAILS_CONTACT_PERSON,
                          )}
                          component={ContactPersonsScreen}
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path={getRouterPath(
                            Routes.ENTERPRISE_DETAILS_CONTACT_PERSON_DETAILS,
                          )}
                          component={ContactPersonDetailScreen}
                        />
                        <PrivateRoute
                          unauthenticatedRedirectUrl={getRouterPath(
                            Routes.LOGIN,
                          )}
                          path={getRouterPath(
                            Routes.ENTERPRISE_DETAILS_VISIBILITY,
                          )}
                          component={VisibilityScreen}
                        />
                      </PrivateRoute>
                      <PrivateRoute
                        unauthenticatedRedirectUrl={getRouterPath(Routes.LOGIN)}
                        path={getRouterPath(Routes.VIDEOS)}
                        component={VideosScreen}
                      />
                      <ForbiddenWrongRegion
                        path={getRouterPath(Routes.FORBIDDEN_WRONG_REGION)}
                      />
                      {
                        // LoginCallback is html page in public folder
                      }
                      <LoginScreen path={getRouterPath(Routes.LOGIN)} />
                      <Redirect
                        from={getPath(Routes.LOGIN_LOCAL)}
                        to={getPath(Routes.LOGIN)}
                        noThrow
                      />
                      <Logout path={getRouterPath(Routes.LOGOUT)} />
                      <MaintenanceScreen
                        path={getRouterPath(Routes.MAINTENANCE)}
                      />
                      {
                        // LogoutCallback is html page in public folder
                      }
                    </Router>
                  </SwMain>
                </SwPage>
                <Footer
                  languages={availableLanguages}
                  regionCode={regionCode as RegionCode}
                />
              </ChangeLanguage>
            </AlertProvider>
          </ActionProvider>
        </AuthProvider>
      </I18nextProvider>
    </div>
  );
};
